import { lazy, Suspense } from 'react';
import { supportingMediaCard, supportingMediaRow, supportingMediaThumbnail, supportingMediaCopy } from '@/features/feedback/components/supportingMediaStyles';
import type { FeedbackPublishedPin, ResourcePin } from '@/lib/types';
import { feedbackPinMediaSelection, type FeedbackPinSource } from './useFeedbackPinRead';

const ResourcePinPreview = lazy(() => import('@/features/resource-reader/ResourcePinPreview').then(module => ({ default: module.ResourcePinPreview })));

/** Pins come from the currently admitted parent read. Thumbnails use its exact
 * current/answered grant, never the Library's broader coach lookup. */
export function FeedbackPinnedResources({ resources, source, onPreview }: {
  resources: FeedbackPublishedPin[]; source: FeedbackPinSource; onPreview?: (pin: ResourcePin) => void;
}) {
  if (!resources.length) return null;
  return <section className="space-y-3" aria-label="Pinned Resources">
    <h4 className="text-sm font-semibold">Pinned Resources</h4>
    {resources.map((pin, index) => <div key={`${pin.resource_id}:${pin.revision_id}`}
      className={`relative min-w-0 text-sm ${supportingMediaCard}`}
      data-resource-id={pin.resource_id} data-revision-id={pin.revision_id}>
      {pin.availability === 'available' && onPreview && <button type="button"
        className="absolute inset-0 z-10 rounded-inset hover:bg-accent/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
        aria-label={`View Resource: ${pin.display_label || `Resource ${index + 1}`}`} aria-haspopup="dialog"
        data-feedback-resource={`${pin.resource_id}:${pin.revision_id}`} onClick={() => onPreview(pin)} />}
      <div className={supportingMediaRow}>
      {pin.availability === 'available' && <div className={supportingMediaThumbnail} data-resource-thumbnail aria-hidden="true">
        <Suspense fallback={<div className="aspect-video rounded-control bg-field" />}>
          <ResourcePinPreview scope={source.scope} resource={pin} selection={feedbackPinMediaSelection(source, pin)} mode="thumbnail" />
        </Suspense>
      </div>}
      <div className={supportingMediaCopy}>
        <strong className="break-words">{pin.availability === 'available' ? pin.display_label || `Resource ${index + 1}` : 'Resource unavailable'}</strong>
        <p className="mt-1 text-xs text-ink-muted">{pin.availability === 'available'
          ? 'Exact pinned version' : 'The exact pinned source is unavailable.'}</p>
      </div>
      </div>
    </div>)}
  </section>;
}
